import { useState, useEffect } from 'react';
import { api } from '../lib/api';

const KANBAN_STATUSES = ['New', 'Planning', 'Content', 'Design', 'Editing', 'Approval', 'Scheduled', 'Published', 'Completed'];

export default function AgencyTasks() {
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [packages, setPackages] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerator, setShowGenerator] = useState(false);
  const [generating, setGenerating] = useState(false);

  const loadTasksData = () => {
    setLoading(true);
    Promise.all([
      api.get('/api/agency-crm/tasks').catch(() => []),
      api.get('/api/client-module/clients').catch(() => []),
      api.get('/api/agency-crm/packages').catch(() => []),
      api.get('/api/staff-module/staff').catch(() => []),
    ])
      .then(([tasksRes, clientsRes, packagesRes, staffRes]) => {
        setTasks(tasksRes || []);
        setClients(clientsRes || []);
        setPackages(packagesRes || []);
        setStaff(staffRes || []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadTasksData();
  }, []);

  const handleGenerateTasks = async (e) => {
    e.preventDefault();
    setGenerating(true);
    const form = e.target;
    const body = {
      clientName: form.clientName.value,
      packageName: form.packageName.value,
      durationMonths: Number(form.durationMonths.value) || 3
    };

    try {
      const res = await api.post('/api/agency-crm/tasks/generate', body);
      alert(res.message || 'Tasks generated successfully!');
      setShowGenerator(false);
      loadTasksData();
    } catch (err) {
      alert('Failed to generate tasks: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await api.put(`/api/agency-crm/tasks/${taskId}`, { status: newStatus });
      // Update local state for fast UI feedback
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    } catch (err) {
      alert('Failed to update task status: ' + err.message);
    }
  };

  const handleStaffAssign = async (taskId, staffName) => {
    try {
      await api.put(`/api/agency-crm/tasks/${taskId}`, { assignedStaff: staffName });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, assignedStaff: staffName } : t));
    } catch (err) {
      alert('Failed to assign staff: ' + err.message);
    }
  };

  if (loading && tasks.length === 0) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Loading CRM Tasks Kanban Board…</div>;
  }

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900, fontSize: '1.4rem' }}>CRM Deliverables & Kanban</h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#64748b' }}>Automatically generate deliverables from package templates and track them on a Kanban board</p>
        </div>
        <button onClick={() => setShowGenerator(prev => !prev)} className="cm-btn-emerald" style={{ background: '#224eae' }}>
          {showGenerator ? 'Cancel' : '🎁 Auto-Generate Package Deliverables'}
        </button>
      </div>

      {showGenerator && (
        <form onSubmit={handleGenerateTasks} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 24, marginBottom: 24, maxWidth: 580 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 800 }}>Auto Task Generator</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 12 }}>
            <div className="cm-field-group">
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 4 }}>Select Client *</label>
              <select name="clientName" required style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <option value="">— Pick client —</option>
                {clients.map(c => <option key={c.id} value={c.clientName}>{c.clientName}</option>)}
              </select>
            </div>
            <div className="cm-field-group">
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 4 }}>Select Package *</label>
              <select name="packageName" required style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <option value="">— Pick package template —</option>
                {packages.map(p => <option key={p.id} value={p.packageName}>{p.packageName}</option>)}
              </select>
            </div>
          </div>
          <div className="cm-field-group" style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 4 }}>Duration (Months)</label>
            <input name="durationMonths" type="number" required defaultValue="3" min="1" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0' }} />
          </div>

          <button type="submit" className="cm-btn-emerald" disabled={generating}>
            {generating ? 'Generating…' : '⚡ Auto-Generate Monthly Tasks'}
          </button>
        </form>
      )}

      {/* Kanban Board Container */}
      <div style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 10, minHeight: 480 }}>
        {KANBAN_STATUSES.map(col => {
          const colTasks = tasks.filter(t => t.status === col);
          return (
            <div key={col} style={{ flex: '0 0 300px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 20, padding: 16, display: 'flex', flexDirection: 'column', gap: 14, height: 'calc(100vh - 280px)', minHeight: 460 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #e2e8f0', paddingBottom: 10, marginBottom: 4 }}>
                <span style={{ fontWeight: 800, fontSize: '0.9rem', color: '#0f172a' }}>{col}</span>
                <span style={{ background: '#e2e8f0', color: '#0f172a', padding: '3px 9px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 800 }}>{colTasks.length}</span>
              </div>

              <div className="cm-kanban-list" style={{ display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto', flex: 1, paddingBottom: 32, paddingRight: 6 }}>
                {colTasks.map(t => (
                  <div key={t.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 16, boxShadow: '0 4px 14px rgba(0,0,0,0.015)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ fontWeight: 800, fontSize: '0.88rem', color: '#0f172a' }}>{t.clientName}</div>
                    <div style={{ fontSize: '0.78rem', color: '#64748b', fontStyle: 'italic' }}>{t.serviceName}</div>
                    
                    {/* Inline controls */}
                    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1px dashed #f1f5f9', paddingTop: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: '0.78rem' }}>
                        <span style={{ color: '#64748b', fontWeight: 600 }}>Status:</span>
                        <select
                          value={t.status}
                          onChange={(e) => handleStatusChange(t.id, e.target.value)}
                          style={{ padding: '4px 8px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.78rem', fontWeight: 700, background: '#fff', width: '100%', maxWidth: 140 }}
                        >
                          {KANBAN_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: '0.78rem' }}>
                        <span style={{ color: '#64748b', fontWeight: 600 }}>Assign:</span>
                        <select
                          value={t.assignedStaff || ''}
                          onChange={(e) => handleStaffAssign(t.id, e.target.value)}
                          style={{ padding: '4px 8px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.78rem', fontWeight: 700, background: '#fff', width: '100%', maxWidth: 140 }}
                        >
                          <option value="Unassigned">Unassigned</option>
                          {staff.filter(st => st.status !== 'Inactive').map(stf => (
                            <option key={stf.id} value={stf.name}>{stf.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
